// src/pages/Auditorias/Configuracoes/index.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useConfigPrompts, temAlteracoes } from '../hooks/useConfigPrompts';
import SeccaoColapsavel from '../components/SeccaoColapsavel';
import BlocoPrompt from './BlocoPrompt';
import '../Auditorias.css';

/**
 * Os prompts do motor da auditoria e o modo das auditorias novas, editáveis sem
 * passar por SQL.
 *
 * Nem todos correm em todas as auditorias: o toggle "Avaliar peça individual"
 * escolhe entre a conferência peça a peça (+ parecer das avaliações) e o
 * parecer único do processo. As secções que não são chamadas ficam marcadas
 * como inactivas.
 *
 * Só os do motor. Os prompts das outras funcionalidades que partilham a tabela
 * (calculo_honorarios, filtrar_acordaos) ficam de fora, e os do projecto Python
 * que vivem em ficheiros .txt não passam por aqui de todo.
 *
 * Gravar substitui — não há histórico nem rollback. Foi decisão do utilizador.
 * O botão de copiar tudo serve de rede: guarda-se o texto antes de mexer.
 */
const Configuracoes = () => {
  const {
    origens, modelos, rascunhos, carregando, erro,
    avaliarPecaIndividual, definirAvaliarPecaIndividual,
    editar, repor, guardar, recarregar,
  } = useConfigPrompts();

  const [aviso, setAviso] = useState(null);
  const [aGravar, setAGravar] = useState(null);
  const [aMudarModo, setAMudarModo] = useState(false);

  const mudarModo = async (valor) => {
    setAviso(null);
    setAMudarModo(true);
    try {
      await definirAvaliarPecaIndividual(valor);
      setAviso(valor
        ? 'Auditorias novas passam a conferir cada peça.'
        : 'Auditorias novas passam a produzir um parecer único do processo.');
    } catch (e) {
      console.error('Erro ao gravar o modo:', e);
      setAviso(e.response?.data?.message || 'Não foi possível gravar o modo.');
    } finally {
      setAMudarModo(false);
    }
  };

  const copiar = (texto, oQue) => {
    if (!texto) {
      setAviso('Nada para copiar.');
      return;
    }
    navigator.clipboard.writeText(texto)
      .then(() => setAviso(`Copiado: ${oQue}.`))
      .catch((e) => {
        console.error('Erro ao copiar:', e);
        setAviso('O browser não deixou copiar para a área de transferência.');
      });
  };

  /**
   * Copia os seis textos de uma vez, com cabeçalhos, tal como estão no ecrã
   * (incluindo alterações por gravar). É a cópia de segurança que o ecrã não
   * tem em base de dados.
   */
  const copiarTudo = () => {
    const partes = origens.map((o) => {
      const r = rascunhos[o.origem] || {};
      const risca = '='.repeat(78);
      return [
        risca,
        `${o.origem} — ${o.titulo}`,
        `modelo: ${r.modelo || '(sem modelo)'} · pensamento: ${r.reasoning_effort || '(não definido)'}`,
        `variáveis: ${o.variaveis.map((v) => `%${v}%`).join(' ')}`,
        risca,
        '',
        '----- SYSTEM -----',
        r.texto_system || '',
        '',
        '----- USER -----',
        r.texto_user || '',
      ].join('\n');
    });

    const cabecalho = `Prompts do motor da Auditoria de Processos\n`
      + `Copiado em ${new Date().toLocaleString('pt-PT')}\n`;

    copiar([cabecalho, ...partes].join('\n\n'),
      `os ${origens.length} prompts (SYSTEM e USER)`);
  };

  const guardarOrigem = async (origem) => {
    setAviso(null);
    setAGravar(origem);
    try {
      return await guardar(origem);
    } finally {
      setAGravar(null);
    }
  };

  return (
    <div className="aud-pagina">
      <div className="aud-cabecalho">
        <div>
          <Link to="/auditorias" className="aud-link">← Auditorias</Link>
          <h1>Configurações dos prompts</h1>
          <p className="aud-sub">
            Os prompts que o motor usa para auditar um processo, e o modo das
            auditorias novas. Gravar tem efeito na execução seguinte — não é
            preciso reiniciar nada.
          </p>
        </div>
        <div className="aud-cabecalho-accoes">
          <button
            type="button"
            className="aud-btn-secundario"
            onClick={copiarTudo}
            disabled={carregando || origens.length === 0}
          >
            COPIAR TODOS OS PROMPTS
          </button>
          <button type="button" className="aud-btn-secundario" onClick={recarregar}>
            ACTUALIZAR
          </button>
        </div>
      </div>

      <p className="aud-cfg-alerta">
        Estes prompts estão em produção e <strong>gravar substitui, sem histórico</strong>.
        Um prompt mal escrito não dá erro no ecrã — dá uma auditoria de dezenas de
        minutos com o resultado errado. Antes de uma alteração de fundo, use
        {' '}<strong>COPIAR TODOS OS PROMPTS</strong> e guarde o texto; depois confirme
        com uma auditoria de teste.
      </p>

      {aviso && <p className="aud-aviso">{aviso}</p>}
      {erro && <p className="aud-erro">{erro}</p>}

      {!carregando && (
        <div className="aud-cfg-modo">
          <div className="aud-cfg-modo-texto">
            <strong>Avaliar peça individual</strong>
            <p className="aud-sub">
              Com <strong>Sim</strong>, o motor confere cada peça de per si e emite o
              parecer a partir dessas avaliações — é lento e caro, mas dá gravidade e
              problemas por peça. Com <strong>Não</strong>, analisa o processo inteiro
              numa só leitura e produz apenas o parecer final.
            </p>
            <p className="aud-sub aud-sub-alerta">
              Vale só para auditorias <strong>novas</strong>. O modo é fixado na
              criação da auditoria e não muda: uma auditoria em modo único
              actualiza sempre em modo único.
            </p>
          </div>
          <div className="aud-cfg-modo-botoes">
            <button
              type="button"
              className={`aud-toggle ${avaliarPecaIndividual ? 'aud-toggle-on' : ''}`}
              onClick={() => mudarModo(true)}
              disabled={aMudarModo || avaliarPecaIndividual}
            >
              SIM
            </button>
            <button
              type="button"
              className={`aud-toggle ${!avaliarPecaIndividual ? 'aud-toggle-on' : ''}`}
              onClick={() => mudarModo(false)}
              disabled={aMudarModo || !avaliarPecaIndividual}
            >
              NÃO
            </button>
          </div>
        </div>
      )}

      {carregando ? (
        <p className="aud-vazio">A carregar…</p>
      ) : (
        origens.map((origem, indice) => {
          const rascunho = rascunhos[origem.origem];
          if (!rascunho) return null;

          const alterado = temAlteracoes(origem, rascunho);

          // null = corre nos dois percursos. Marcar o que não é chamado evita
          // afinar durante meia hora um prompt que o motor nunca lê.
          const emUso = origem.quando_avalia_peca === null
            || origem.quando_avalia_peca === avaliarPecaIndividual;

          return (
            <SeccaoColapsavel
              key={origem.origem}
              defaultOpen={indice === 0}
              titulo={origem.titulo}
              badge={
                <>
                  <span className="aud-mono">{origem.origem}</span>
                  {!emUso && <span className="aud-badge aud-cfg-inactivo">inactivo</span>}
                </>
              }
              resumo={alterado ? 'por gravar' : ''}
            >
              <BlocoPrompt
                definicao={origem}
                rascunho={rascunho}
                alterado={alterado}
                aGravar={aGravar === origem.origem}
                modelos={modelos}
                onEditar={(campo, valor) => editar(origem.origem, campo, valor)}
                onGuardar={() => guardarOrigem(origem.origem)}
                onRepor={() => repor(origem.origem)}
                onCopiar={copiar}
              />
            </SeccaoColapsavel>
          );
        })
      )}
    </div>
  );
};

export default Configuracoes;
